/**
 * Mix portfolio images across categories.
 *
 * The portfolio API returns images grouped by category, and every consumer
 * wants the same thing from them: a flat list that does not show eight
 * embroidered hats in a row because hats happen to be the first category. A
 * round robin takes one from each in turn, so the first row of any grid shows
 * the range of what the shop does rather than the alphabetical accident of how
 * the folders are named.
 */
export type PortfolioCategory = { slug: string; label: string; images: string[] };

export function interleaveCategories(
  cats: PortfolioCategory[],
  limit = Infinity,
): string[] {
  if (!Array.isArray(cats)) return [];
  const lists = cats.map((c) => (Array.isArray(c.images) ? c.images : []));
  const out: string[] = [];
  const seen = new Set<string>();

  for (let i = 0; out.length < limit; i++) {
    let added = false;
    for (const list of lists) {
      const url = list[i];
      if (!url || seen.has(url)) continue;
      // The same file can legitimately be filed under two categories, and the
      // duplicate is far more noticeable in a grid than a missing tile.
      seen.add(url);
      out.push(url);
      added = true;
      if (out.length >= limit) break;
    }
    if (!added) break;
  }
  return out;
}
